import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@sonaemc/mantra-react";

import Divider from "@/components/divider";
import { Button } from "@/components/ui/button";
import Header from "@/components/header/Header";
import ButtonContainer from "@/components/button-container";
import ErrorMessage from "@/components/error-message/error-message";
import NeedHelpButton from "@/components/need-help-button/need-help-button";

import { tryCatch } from "@/utils/try-catch";

import { useApiResponseHandler } from "@/hooks/useApiResponseHandler";

import { AuthFlowType, FLOW_CONFIGS, FlowConfig } from "@/config/LoyaltyCardOtpOptionsFlowConfig";

import { genericService } from "@/services/GenericService";

import { LoyaltyCardAssociationResponse } from "@/models/shared/loyalty/LoyaltyCardAssociationResponse";
import { LoyaltyCardOtpOptionsResponse } from "@/models/continente-credentials/loyaltycard/LoyaltyCardOtpOptionsResponse";

interface AssociateIdentifierPageProps {
  flowConfig: FlowConfig;
}

function SharedLoyaltyCardOtpOptions({ flowConfig }: AssociateIdentifierPageProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { handleResponse } = useApiResponseHandler();
  const location = useLocation<LoyaltyCardAssociationResponse>();

  const nextStep = location?.state?.nextStep || "";

  const {
    outDatedNextUrl = "",
    loyaltyCardOtpOptions = []
  } = location.state.properties || {};

  const options = loyaltyCardOtpOptions.map((o) => ({
    type: o.type,
    text: o.contact,
    value: o.type.toString()
  }))

  const handleOudatedLinkOnClick = async () => {
    setLoading(true);

    const { data: response, error } = await tryCatch(genericService.post<LoyaltyCardAssociationResponse>(outDatedNextUrl));
    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  const handleOnSelect = async (option: string) => {
    setLoading(true);

    const payload = {
      selectedOption: option,
    };

    const { data: response, error } = await tryCatch(genericService.post<LoyaltyCardOtpOptionsResponse, any>(nextStep, payload));
    handleResponse(response, {
      setError
    }, error);

    setLoading(false);
  };

  return (
    <div className="flex flex-col justify-self-center items-center gap-10 pb-2 w-full">
      <Header
        title={t(flowConfig.labels.headerTitle)}
        subtitle={t(flowConfig.labels.headerSubtitle || "")}
        showLoading={loading}
        subTitleAriaHidden={false}
        subTitleAriaLabel={t(flowConfig.labels.headerSubtitle || "")}
        previousStepBackXTimes={1}
      />
      <div aria-label={t(flowConfig.labels.headerSubtitle || "")}>
        <div role="list" aria-label="Opções" className="min-w-96 flex gap-8 flex-col">
          {options.sort((a, b) => b.type.localeCompare(a.type)).map((o, index) => {
            return (
              <Button
                key={index}
                disabled={loading}
                aria-label={o.type === "MobilePhone" ? flowConfig.labels.inputAria.mobile : flowConfig.labels.inputAria.email} onClick={loading ? () => { } : () => handleOnSelect(o.value)}>
                <div aria-hidden className="flex items-center gap-10">
                  {o.type &&
                    <Icon size="lg"
                      className="text-[var(--on-surface-primary-fixed)]"
                      name={o.type === "MobilePhone" ? "StreamlineDevicesPhoneHandHold1Bold" : "StreamlineEmailsEnvelopeRegular"} />
                  }
                  {o.text}
                </div>
              </Button>
            );
          })}
          <Divider title="Ou" />
          <ButtonContainer>
            <Button
              variant={"secondary"}
              onClick={handleOudatedLinkOnClick}
              disabled={loading}
            >
              {t(flowConfig.labels.outDatedButton)}
            </Button>
            <NeedHelpButton />
          </ButtonContainer>
        </div >
      </div>
      <ErrorMessage errorString={error} />
    </div>
  );
}

const createLoyaltyCardOtpOptions = (flowConfig: AuthFlowType) => {
  const config = FLOW_CONFIGS[flowConfig];
  return () => <SharedLoyaltyCardOtpOptions flowConfig={config} />;
};

export const LoyaltyCardOtpOptions = createLoyaltyCardOtpOptions(AuthFlowType.CREDENTIALS);
export const SCLoyaltyCardOtpOptions = createLoyaltyCardOtpOptions(AuthFlowType.SITE_CREDENTIALS); 
